// frontend/src/pages/admin/AuditLogPage.tsx
import { useCallback, useEffect, useState } from "react";
import { api, type AuditLogEntry } from "../../lib/api";

const PAGE_SIZE = 50;

const EVENT_OPTIONS = [
  "",
  "create_artifact",
  "update_artifact",
  "delete_artifact",
  "renew_artifact",
  "view_artifact",
  "change_visibility",
  "artifact_sharing_changed",
  "edit_lease_claimed",
  "edit_lease_stolen",
  "edit_version_conflict",
  "artifact_manifest_change_rejected",
  "restore_artifact_version",
  "force_delete_artifact",
  "restore_artifact",
  "admin_view_artifact",
  "admin_user_invite",
  "admin_user_role_change",
  "admin_user_deactivate",
  "admin_user_reactivate",
  "admin_quota_change",
  "mcp_token_create",
  "mcp_token_revoke",
  "mcp_auth_failure",
  "purge_run",
  "anthropic_proxy_call",
  "oauth_register_client",
  "oauth_authorize",
  "oauth_token_issue",
];

const OUTCOME_OPTIONS = ["", "success", "denied", "error"];

function formatTime(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const OUTCOME_CHIP: Record<string, string> = {
  success: "bg-emerald-100 text-emerald-800",
  denied: "bg-amber-100 text-amber-800",
  error: "bg-rose-100 text-rose-800",
};

/** Admin → Audit log sub-page. Paginated, filterable table over audit_log. */
export function AuditLogPage() {
  const [rows, setRows] = useState<AuditLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [actor, setActor] = useState("");
  const [eventType, setEventType] = useState("");
  const [outcome, setOutcome] = useState("");
  const [since, setSince] = useState("");
  const [page, setPage] = useState(0);

  const load = useCallback(() => {
    setError(null);
    api.admin
      .auditLog({
        actor: actor || undefined,
        event_type: eventType || undefined,
        outcome: outcome || undefined,
        since: since ? new Date(since).toISOString() : undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, [actor, eventType, outcome, since, page]);

  useEffect(load, [load]);

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Audit log</h1>
      <p className="mb-4 text-sm text-slate-500">
        Every mutating action (create / update / delete / role change / etc.)
        as well as MCP auth failures and admin overrides. Newest first.
      </p>

      <section className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-5">
        <input
          type="search"
          placeholder="Actor email"
          value={actor}
          onChange={(e) => {
            setActor(e.target.value);
            setPage(0);
          }}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        />
        <select
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setPage(0);
          }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        >
          {EVENT_OPTIONS.map((ev) => (
            <option key={ev} value={ev}>
              {ev || "Event: any"}
            </option>
          ))}
        </select>
        <select
          value={outcome}
          onChange={(e) => {
            setOutcome(e.target.value);
            setPage(0);
          }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        >
          {OUTCOME_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o || "Outcome: any"}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={since}
          onChange={(e) => {
            setSince(e.target.value);
            setPage(0);
          }}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        />
        <button
          type="button"
          onClick={() => {
            setActor("");
            setEventType("");
            setOutcome("");
            setSince("");
            setPage(0);
          }}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          Clear filters
        </button>
      </section>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {rows === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-slate-500">No matching entries.</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((r) => (
                  <>
                    <tr
                      key={r.id}
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                        {formatTime(r.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <div>{r.actor_email || "—"}</div>
                        <div className="text-xs text-slate-500">{r.actor_kind}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.event_type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        {r.target_slug || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${OUTCOME_CHIP[r.outcome] || "bg-slate-100 text-slate-700"}`}
                        >
                          {r.outcome}
                        </span>
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr key={r.id + "-detail"}>
                        <td colSpan={5} className="bg-slate-50 px-3 py-2">
                          <div className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                            <div>
                              <span className="text-slate-500">IP:</span>{" "}
                              <code>{r.ip || "—"}</code>
                            </div>
                            <div>
                              <span className="text-slate-500">User agent:</span>{" "}
                              <code className="break-all">
                                {r.user_agent || "—"}
                              </code>
                            </div>
                            <div className="sm:col-span-2">
                              <span className="text-slate-500">Metadata:</span>
                              <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-xs">
                                {JSON.stringify(r.metadata, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              Page {page + 1} · {rows.length} row{rows.length === 1 ? "" : "s"}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded border border-slate-200 px-3 py-1 text-xs disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={rows.length < PAGE_SIZE}
                className="rounded border border-slate-200 px-3 py-1 text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
